<script setup lang="ts">
import type { RequestUpdateCustomerEmailAddressMutationVariables } from '#graphql-operations'
import { useRequestUpdateCustomerEmailAddressSchema } from '@layers/common/app/schemas'
import { InvalidCredentialsError } from '@layers/common/app/services/errors'

const props = defineProps<{
  initialValues?: RequestUpdateCustomerEmailAddressMutationVariables | null
}>()

const { t } = useI18n()
const { toast } = useToast()
const { requestUpdateCustomerEmailAddress } = useActiveCustomer()

const openConfirmationDialog = ref(false)

const { isSubmitting, handleSubmit, resetForm, setFieldError, setValues, values, isFieldValid } = useForm<RequestUpdateCustomerEmailAddressMutationVariables>({
  validationSchema: useRequestUpdateCustomerEmailAddressSchema(),
  initialValues: {
    emailAddress: props.initialValues?.emailAddress ?? '',
    password: props.initialValues?.password ?? '',
  },
})

function onOpenConfirmationDialog(opening: boolean) {
  if (!values.emailAddress || !isFieldValid('emailAddress') || values.emailAddress === props.initialValues?.emailAddress) {
    openConfirmationDialog.value = false
    return
  }
  openConfirmationDialog.value = opening
}

const errorMessage = ref('')

const onSubmit = handleSubmit(async (values) => {
  try {
    await requestUpdateCustomerEmailAddress(values)
    resetForm()
    toast({
      variant: 'success',
      title: t('request_update_customer_email_address.form.success'),
    })
    openConfirmationDialog.value = false
  }
  catch (error) {
    if (error instanceof InvalidCredentialsError) {
      setFieldError('password', error.message)
      console.error(error)
      return
    }
    else if (error instanceof Error) {
      errorMessage.value = error.message
    }
    else {
      errorMessage.value = t('validation.unknown.message')
    }
    console.error(error)
    openConfirmationDialog.value = false
  }
})

watchOnce(() => props.initialValues, (initialValues) => {
  setValues({
    emailAddress: initialValues?.emailAddress || '',
    password: initialValues?.password || '',
  })
}, { immediate: true })
</script>

<template>
  <form @submit="onSubmit">
    <UiAlert v-if="errorMessage" variant="destructive" class="mb-4">
      <Icon name="heroicons:exclamation-circle" mode="svg" />
      <UiAlertTitle>{{ errorMessage }}</UiAlertTitle>
    </UiAlert>

    <div class="space-y-4">
      <UiFormField v-slot="{ componentField }" name="emailAddress" :validate-on-model-update="false" :validate-on-blur="false">
        <UiFormItem v-auto-animate>
          <UiFormLabel>{{ t('form.email_address.label') }}*</UiFormLabel>
          <UiFormControl>
            <UiInput type="email" required autocomplete="email" enterkeyhint="next" data-testid="emailAddress" v-bind="componentField" />
          </UiFormControl>
          <UiFormMessage />
        </UiFormItem>
      </UiFormField>
    </div>

    <div class="mt-6 py-3 text-right">
      <UiDialog v-model:open="openConfirmationDialog" @update:open="onOpenConfirmationDialog">
        <UiDialogTrigger as-child>
          <UiButton
            data-testid="updateCustomerEmailAddress"
            variant="success"
            class="w-full rounded-full sm:w-auto"
          >
            {{ t('update_customer_email_address.button.submit') }}
          </UiButton>
        </UiDialogTrigger>

        <UiDialogContent>
          <UiDialogHeader>
            <UiDialogTitle>
              {{ t('request_update_customer_email_address.confirm_email_change') }}
            </UiDialogTitle>
            <UiDialogDescription>
              {{ t('request_update_customer_email_address.confirm_email_change_description') }}
            </UiDialogDescription>
          </UiDialogHeader>
          <UiFormField v-slot="{ componentField }" name="password" :validate-on-model-update="false" :validate-on-blur="false">
            <UiFormItem v-auto-animate>
              <UiFormLabel>{{ t('form.password.label') }}*</UiFormLabel>
              <UiFormControl>
                <UiInput type="password" required autocomplete="current-password" enterkeyhint="go" data-testid="password" v-bind="componentField" />
              </UiFormControl>
              <UiFormMessage />
            </UiFormItem>
          </UiFormField>

          <UiButton
            type="submit"
            variant="success"
            data-testid="requestUpdateCustomerEmailAddress"
            :disabled="isSubmitting"
            class="w-full rounded-full sm:w-auto"
            @click="onSubmit"
          >
            {{ t('form.submit.label') }}
          </UiButton>
        </UiDialogContent>
      </UiDialog>
    </div>
  </form>
</template>
